import { Dispatch, SetStateAction } from 'react';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { generateAnalysisImagePrompts } from '../../../api';
import { formatDate } from '../../../utils/formatDate';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface SearchSummary {
  assetCount: number;
  time: string;
}

interface ProjectConfirmDialogsProps {
  projectId?: string;
  t: Translate;
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  confirmGenPromptsOpen: boolean;
  confirmReanalyzeOpen: boolean;
  confirmResearchOpen: boolean;
  confirmCategorySearch: { cat: number; label: string } | null;
  confirmBatchSearch: { category: number; label: string; count: number } | null;
  confirmReanalyzeStep: 1 | 2;
  analysisStatus?: string;
  analysisIsAdvanced?: boolean;
  searchSummary: SearchSummary | null;
  generatingPrompts: boolean;
  retryingAnalysis: boolean;
  setConfirmGenPromptsOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmReanalyzeOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmReanalyzeStep: Dispatch<SetStateAction<1 | 2>>;
  setConfirmResearchOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmCategorySearch: Dispatch<SetStateAction<{ cat: number; label: string } | null>>;
  setConfirmBatchSearch: Dispatch<SetStateAction<{ category: number; label: string; count: number } | null>>;
  setGeneratingPrompts: Dispatch<SetStateAction<boolean>>;
  loadAnalysis: (projectId: string) => Promise<void | boolean>;
  handleReanalyzeConfirm: () => void | Promise<void>;
  handleTriggerSearch: (category?: number) => void;
  handleBatchSearchConfirmed: (category: number) => void;
}

export default function ProjectConfirmDialogs({
  projectId,
  t,
  toast,
  confirmGenPromptsOpen,
  confirmReanalyzeOpen,
  confirmResearchOpen,
  confirmCategorySearch,
  confirmBatchSearch,
  confirmReanalyzeStep,
  analysisStatus,
  analysisIsAdvanced,
  searchSummary,
  generatingPrompts,
  retryingAnalysis,
  setConfirmGenPromptsOpen,
  setConfirmReanalyzeOpen,
  setConfirmReanalyzeStep,
  setConfirmResearchOpen,
  setConfirmCategorySearch,
  setConfirmBatchSearch,
  setGeneratingPrompts,
  loadAnalysis,
  handleReanalyzeConfirm,
  handleTriggerSearch,
  handleBatchSearchConfirmed,
}: ProjectConfirmDialogsProps) {
  return (
    <>
      <ConfirmDialog
        open={confirmGenPromptsOpen}
        onCancel={() => setConfirmGenPromptsOpen(false)}
        onConfirm={async () => {
          if (!projectId) return;
          setGeneratingPrompts(true);
          try {
            await generateAnalysisImagePrompts(projectId);
            setConfirmGenPromptsOpen(false);
            await loadAnalysis(projectId);
          } catch (err: any) {
            console.error('生成图片提示词失败:', err);
          } finally {
            setGeneratingPrompts(false);
          }
        }}
        title={analysisStatus === 'extracted' ? t('confirmDialogs.genPrompts.titleExtracted') : t('confirmDialogs.genPrompts.titleRegenerate')}
        message={
          analysisStatus === 'extracted'
            ? t('confirmDialogs.genPrompts.messageExtracted')
            : t('confirmDialogs.genPrompts.messageRegenerate')
        }
        confirmText={t('confirm', { ns: 'common' })}
        loading={generatingPrompts}
      />

      <ConfirmDialog
        open={confirmReanalyzeOpen}
        onCancel={() => { setConfirmReanalyzeOpen(false); setConfirmReanalyzeStep(1); }}
        onConfirm={handleReanalyzeConfirm}
        title={t('confirmDialogs.reanalyze.title')}
        message={
          confirmReanalyzeStep === 1
            ? t('confirmDialogs.reanalyze.step1Message')
            : analysisIsAdvanced
              ? t('confirmDialogs.reanalyze.step2Advanced')
              : t('confirmDialogs.reanalyze.step2Basic')
        }
        confirmText={confirmReanalyzeStep === 1 ? t('confirm', { ns: 'common' }) : t('confirmDialogs.reanalyze.step2Confirm')}
        confirmDanger
        loading={retryingAnalysis}
      />

      <ConfirmDialog
        open={confirmResearchOpen}
        onCancel={() => setConfirmResearchOpen(false)}
        onConfirm={() => {
          setConfirmResearchOpen(false);
          handleTriggerSearch();
        }}
        title={t('confirmDialogs.researchKeywords.title')}
        message={
          searchSummary
            ? t('confirmDialogs.researchKeywords.messageExisting', { count: searchSummary.assetCount, time: formatDate(searchSummary.time) })
            : t('confirmDialogs.researchKeywords.messageNew')
        }
        confirmText={t('confirmDialogs.researchKeywords.confirm')}
      />

      <ConfirmDialog
        open={!!confirmCategorySearch}
        onCancel={() => setConfirmCategorySearch(null)}
        onConfirm={() => {
          if (confirmCategorySearch) {
            handleTriggerSearch(confirmCategorySearch.cat);
          }
          setConfirmCategorySearch(null);
        }}
        title={t('confirmDialogs.categorySearch.title', { label: confirmCategorySearch?.label || '' })}
        message={t('confirmDialogs.categorySearch.message', { label: confirmCategorySearch?.label || '' })}
        confirmText={t('confirm', { ns: 'common' })}
      />

      <ConfirmDialog
        open={!!confirmBatchSearch}
        onCancel={() => setConfirmBatchSearch(null)}
        onConfirm={() => {
          if (confirmBatchSearch) {
            handleBatchSearchConfirmed(confirmBatchSearch.category);
          }
          setConfirmBatchSearch(null);
        }}
        title={t('confirmDialogs.batchSearch.title')}
        message={confirmBatchSearch ? t('confirmDialogs.batchSearch.message', { count: confirmBatchSearch.count, label: confirmBatchSearch.label }) : ''}
        confirmText={t('confirmDialogs.batchSearch.confirm')}
      />
    </>
  );
}
